import { useFieldInfo, useTsController } from "@ts-react/form";
import React, { useId } from "react";
import { Adapt, Separator as DefaultSeparator, Fieldset, getFontSize, isWeb, Label, Select, type SelectProps, Sheet, styled, Theme, XStack, YStack } from "tamagui";
import { LinearGradient } from "@tamagui/linear-gradient";
import { Check, ChevronDown, ChevronUp } from "@tamagui/lucide-icons";
import { FieldError } from "../FieldError";
import { useFormFieldOptions } from "./FormFieldOptionsProvider";

// Copied from Separator.tsx but importing it directly is broken
const Separator = styled(DefaultSeparator, {
  borderColor: "$strokePrimary",
  borderBottomWidth: "$0.5"
});

// Coppied from Separator.tsx but importing it directly is broken
const WithSeparator = ({
  separator,
  children
}: {
  separator?: React.ReactNode;
  children: React.ReactNode;
}) => {
  return <>
      {React.Children.toArray(children).map((child, index, array) => <React.Fragment key={index}>
          {child}
          {index < array.length - 1 ? separator ?? <Separator /> : null}
        </React.Fragment>)}
    </>;
};
type SelectItem = {
  value: string;
  name: string;
};
export const SelectField = ({
  options,
  native = true,
  ...props
}: {
  options: SelectItem[];
} & Pick<SelectProps, "size" | "native">) => {
  const {
    field,
    error,
    formState: {
      isSubmitting
    }
  } = useTsController<string>();
  const {
    label,
    isOptional
  } = useFieldInfo();
  const {
    hideOptionalLabel
  } = useFormFieldOptions();
  const id = useId();
  const disabled = isSubmitting;
  return <Theme name={error ? "red_alt2" : "granted"} forceClassName data-sentry-element="Theme" data-sentry-component="SelectField" data-sentry-source-file="SelectField.tsx">
      <Fieldset data-sentry-element="Fieldset" data-sentry-source-file="SelectField.tsx">
        {label ? <Label size={props.size || "$3"} htmlFor={id} pb="$1" pl={0}>
            {label}
            {isOptional && !hideOptionalLabel ? ` (Optional)` : null}
          </Label> : null}
        <Select native={native} id={id} value={field.value} onValueChange={field.onChange} zIndex={200000} {...props} data-sentry-element="Select" data-sentry-source-file="SelectField.tsx">
          <Select.Trigger width="100%" iconAfter={ChevronDown} bw={field.value ? "$1" : "$0.5"} m={field.value ? "$0" : "$0.5"} boc={field.value ? "$borderColorFocus" : "$borderColor"} cursor="pointer" hoverStyle={{
          bg: "$backgroundHover"
        }} data-sentry-element="Select.Trigger" data-sentry-source-file="SelectField.tsx">
            <Select.Value data-sentry-element="Select.Value" data-sentry-source-file="SelectField.tsx" />
          </Select.Trigger>

          <Adapt when="sm" platform="touch" data-sentry-element="Adapt" data-sentry-source-file="SelectField.tsx">
            <Sheet native modal dismissOnSnapToBottom transition="overlay" snapPointsMode="fit" data-sentry-element="Sheet" data-sentry-source-file="SelectField.tsx">
              <Sheet.Frame data-sentry-element="Sheet.Frame" data-sentry-source-file="SelectField.tsx">
                <Sheet.ScrollView contentContainerStyle={{
                paddingBottom: "$5"
              }} data-sentry-element="Sheet.ScrollView" data-sentry-source-file="SelectField.tsx">
                  <Adapt.Contents data-sentry-element="Adapt.Contents" data-sentry-source-file="SelectField.tsx" />
                </Sheet.ScrollView>
              </Sheet.Frame>
              <Sheet.Overlay backgroundColor="$background05" data-sentry-element="Sheet.Overlay" data-sentry-source-file="SelectField.tsx" />
            </Sheet>
          </Adapt>

          <Select.Content data-sentry-element="Select.Content" data-sentry-source-file="SelectField.tsx">
            <Select.ScrollUpButton alignItems="center" justifyContent="center" position="relative" width="100%" height="$3" data-sentry-element="Select.ScrollUpButton" data-sentry-source-file="SelectField.tsx">
              <YStack zIndex={10} data-sentry-element="YStack" data-sentry-source-file="SelectField.tsx">
                <ChevronUp size={20} data-sentry-element="ChevronUp" data-sentry-source-file="SelectField.tsx" />
              </YStack>
              <LinearGradient start={[0, 0]} end={[0, 1]} fullscreen colors={["$background", "$backgroundTransparent"]} borderRadius="$4" data-sentry-element="LinearGradient" data-sentry-source-file="SelectField.tsx" />
            </Select.ScrollUpButton>

            <Select.Viewport minWidth={200} data-sentry-element="Select.Viewport" data-sentry-source-file="SelectField.tsx">
              <XStack als="flex-start" data-sentry-element="XStack" data-sentry-source-file="SelectField.tsx">
                <Select.Group disabled={disabled} data-sentry-element="Select.Group" data-sentry-source-file="SelectField.tsx">
                  <WithSeparator separator={<Separator />} data-sentry-element="WithSeparator" data-sentry-source-file="SelectField.tsx">
                    {options.map((item, i) => {
                    return <Select.Item index={i} key={item.name} value={item.value}>
                          <Select.ItemText>{item.name}</Select.ItemText>
                          <Select.ItemIndicator marginLeft="auto">
                            <Check size={16} />
                          </Select.ItemIndicator>
                        </Select.Item>;
                  })}
                  </WithSeparator>
                </Select.Group>
                {/* special icon treatment for native */}
                {native && isWeb ? <YStack fullscreen alignItems="center" justifyContent="center" width="$4" style={{
                pointerEvents: "none"
              }}>
                    <ChevronDown size={getFontSize((props.size ?? "$true") as number)} />
                  </YStack> : null}
              </XStack>
            </Select.Viewport>

            <Select.ScrollDownButton alignItems="center" justifyContent="center" position="relative" width="100%" height="$3" data-sentry-element="Select.ScrollDownButton" data-sentry-source-file="SelectField.tsx">
              <YStack zIndex={10} data-sentry-element="YStack" data-sentry-source-file="SelectField.tsx">
                <ChevronDown size={20} data-sentry-element="ChevronDown" data-sentry-source-file="SelectField.tsx" />
              </YStack>
              <LinearGradient start={[0, 0]} end={[0, 1]} fullscreen colors={["$backgroundTransparent", "$background"]} borderRadius="$4" data-sentry-element="LinearGradient" data-sentry-source-file="SelectField.tsx" />
            </Select.ScrollDownButton>
          </Select.Content>
        </Select>
        <FieldError message={error?.errorMessage} data-sentry-element="FieldError" data-sentry-source-file="SelectField.tsx" />
      </Fieldset>
    </Theme>;
};